import { LabExam } from '../LabExam';
import { BadgeTone, PdfRegistryCard, PdfSummaryData, PdfTotalCard } from '../types/registryPdf';
import { formatDate } from './dateUtils';
import { SummaryCategory, buildTotalCard, formatFilterDescription, normalize } from './registryPdfUtils';

const NO_DATA = 'Нет данных';

const RESULT_CATEGORIES: { label: string; color: string; tone: BadgeTone }[] = [
    { label: 'Не соответствует НД (фальсификат)', color: 'rgb(237, 93, 98)', tone: 'negative' },
    { label: 'Не соответствует НД (брак)', color: 'rgb(255, 166, 0)', tone: 'warning' },
    { label: 'Соответствует НД', color: 'rgb(126, 178, 70)', tone: 'positive' },
    { label: NO_DATA, color: 'rgb(196, 196, 196)', tone: 'neutral' },
];
const OTHER_RESULT_COLORS = ['#6f42c1', '#17a2b8', '#6c757d', '#996633'];


// Пустой результат и "нет данных" одна категория, как на странице
const getExamResult = (exam: LabExam): string => {
    const result = (exam.exam_result || '').trim();
    if (!result || normalize(result) === normalize(NO_DATA)) return NO_DATA;
    return result;
};


const getExamYear = (exam: LabExam): string => {
    if (!exam.exam_date) return '';
    const date = new Date(exam.exam_date);
    return isNaN(date.getTime()) ? '' : String(date.getFullYear());
};


// В данных пустые значения часто записаны словами «нет данных»
const hasValue = (text: string | null | undefined): boolean => {
    return !!text && normalize(text) !== normalize(NO_DATA);
};


export const describeLabExamFilters = (
    examResults: string[],
    years: string[],
    regions: string[],
    filters: {
        manufacturerName: string[];
        manufacturerInn: string[];
        region: string[];
        objectAddress: string[];
        weldingJointType?: string[];
    },
): string => {
    return formatFilterDescription([
        { label: 'результат', values: examResults },
        { label: 'год', values: [...years].sort(), quoted: false },
        { label: 'регион на карте', values: regions },
        { label: 'производитель', values: filters.manufacturerName },
        { label: 'ИНН производителя', values: filters.manufacturerInn },
        { label: 'регион', values: filters.region },
        { label: 'адрес объекта', values: filters.objectAddress },
        { label: 'тип сварного соединения', values: filters.weldingJointType || [] },
    ]);
};


export const buildExamResultsCard = (title: string, exams: LabExam[]): PdfTotalCard => {
    const categories: SummaryCategory<LabExam>[] = [];
    RESULT_CATEGORIES.forEach(category => {
        if (exams.some(exam => getExamResult(exam) === category.label)) {
            categories.push({ label: category.label, color: category.color, matches: exam => getExamResult(exam) === category.label });
        }
    });

    const knownResults = RESULT_CATEGORIES.map(category => category.label);
    const otherResults: string[] = [];
    exams.forEach(exam => {
        const result = getExamResult(exam);
        if (!knownResults.includes(result) && !otherResults.includes(result)) otherResults.push(result);
    });
    otherResults.forEach((result, index) => {
        categories.push({ label: result, color: OTHER_RESULT_COLORS[index % OTHER_RESULT_COLORS.length], matches: exam => getExamResult(exam) === result });
    });
    return buildTotalCard(title, exams, categories, getExamYear, true);
};


export const buildLabExamsSummary = (
    exams: LabExam[],
    filterDescription: string,
    dataDate: string | null,
    weldedRegistry = false,
): PdfSummaryData => {
    let subtitle = `Фильтр: ${filterDescription}`;
    if (dataDate) subtitle += ` · данные от ${dataDate}`;

    return {
        title: weldedRegistry
            ? 'Отчёт по реестру испытаний сварных соединений'
            : 'Отчёт по реестру лабораторных испытаний',
        subtitle,
        counters: [
            { label: 'Общее количество испытаний', value: exams.length },
            { label: 'Общее количество производителей', value: new Set(exams.map(exam => exam.inn_company)).size },
        ],
        cards: [buildExamResultsCard(
            weldedRegistry
                ? 'Испытания сварных соединений. Соотношение результатов'
                : 'Лабораторные испытания. Соотношение результатов',
            exams,
        )],
    };
};


export const buildLabExamRegistryCard = (exam: LabExam): PdfRegistryCard => {
    const result = getExamResult(exam);
    const category = RESULT_CATEGORIES.find(resultCategory => resultCategory.label === result);
    const manufacturerHints = [exam.region_company, exam.state_company, exam.address_company].filter(hasValue);

    return {
        number: exam.name_company || '—',
        status: { text: result, tone: category ? category.tone : 'neutral' },
        fields: [
            { label: 'Дата фиксации', entries: [{ value: exam.exam_date ? formatDate(exam.exam_date) : '' }] },
            {
                label: 'Производитель',
                entries: [{
                    value: hasValue(exam.inn_company) ? `ИНН ${exam.inn_company}` : 'нет данных',
                    hint: manufacturerHints.join(' · '),
                }],
            },
            { label: 'Объект', entries: [{ value: exam.object_name, hint: hasValue(exam.object_adres) ? exam.object_adres : '' }] },
            { label: 'Заказчик', entries: [{ value: exam.customer }] },
            { label: 'Основание обращения', entries: [{ value: exam.reason_request }] },
            { label: 'Срок эксплуатации', entries: [{ value: exam.exploitation_time }] },
            ...(hasValue(exam.welding_joint_type)
                ? [{ label: 'Тип сварного соединения', entries: [{ value: exam.welding_joint_type || '' }] }]
                : []),
        ],
        sections: [
            { title: 'Наименование продукции', text: exam.product_name },
            { title: 'Техническая документация', text: exam.technical_documentation },
            { title: 'Соответствие требованиям', text: exam.compliance_requirements },
        ],
    };
};
